/**
 * exportAppendix — the "Appendix — cited evidence" section appended to every
 * exported report (.md / .pdf / .docx).
 *
 * WHY IT EXISTS. A weave answer cites its evidence as `[label](artifact://id)`.
 * Export reduced every one of those links to its bare label (`exportArtifactMarkdown`
 * → `stripArtifactLinks`), because the `artifact://` scheme means nothing outside the
 * app. The result was a downloadable report whose every claim pointed at data the
 * file did not contain: "127 classes reference Opportunity.StageName (Field usage
 * scan)" with no scan in it. The reader could not check a single number without
 * going back to the app — which is the one thing an exported report exists to avoid.
 *
 * WHAT IT DOES. The label stays inline (unchanged); the DATA moves to an appendix at
 * the end of the document, one entry per distinct cited artifact, in citation order.
 * Data artifacts render as a table (or a CSV block when too wide to table), code as a
 * fenced block, everything else as its text.
 *
 * WHY PLAIN MARKDOWN. The three exporters share one input: `downloadMarkdownAs` emits
 * .md verbatim, renders the same markdown through react-markdown for the PDF print
 * window, and parses it block-by-block for the .docx. Appending markdown is therefore
 * the only representation that lands in all three formats at once — a bespoke
 * appendix per format would be three chances to diverge.
 *
 * WHY THE CAPS ARE WHAT THEY ARE. An exhaustive graph run can carry tens of thousands
 * of rows; pasting them all into a .docx is a hang, not a feature. Each artifact
 * contributes at most `APPENDIX_ROW_CAP` rows and then says — honestly, with the real
 * remainder — where the rest is. A truncation the document admits to is fine; a
 * truncation it hides is the bug this file was written to fix.
 */
import { getArtifact } from "./api";
import {
  collectArtifactCitations,
  exportArtifactMarkdown,
  sanitizeArtifactLabel,
  type ArtifactCitation,
} from "./artifactDisplay";
import { dataColumns, displayCell, pickDataRows, readDataCell } from "./dataRows";

/** Rows per artifact in the appendix. Generous — the cap exists to stop a .docx
 *  build from hanging, not to editorialize about what the user may see. */
export const APPENDIX_ROW_CAP = 2000;

/**
 * Rows the WHOLE appendix may contribute, spent in citation order.
 *
 * The per-artifact cap alone is not a bound on the document: a report citing ten
 * exhaustive scans multiplies it by ten, and 20,000 table rows is precisely the
 * .docx hang the per-artifact cap was introduced to prevent. Earlier citations are
 * the ones the reader is most likely to be checking, so the budget is spent in the
 * order the report cites them and every entry that gets less than its rows says so.
 */
export const APPENDIX_TOTAL_ROW_BUDGET = 5000;

/** Lines per code/text artifact in the appendix, same reasoning as the row cap. */
export const APPENDIX_LINE_CAP = 400;

/** Wider than this and a markdown table is unreadable in a PDF column — those
 *  render as a CSV block instead, which also survives a copy-paste into a sheet. */
const MAX_TABLE_COLUMNS = 8;

export const APPENDIX_HEADING = "Appendix — cited evidence";

export interface AppendixArtifact {
  id: string;
  name?: string;
  type?: string;
  language?: string | null;
  content: string;
}

/** Resolves an artifact id to its content, or null when it cannot be loaded. */
export type AppendixResolver = (id: string) => Promise<AppendixArtifact | null>;

/** Escape a cell for a markdown table row: pipes end the cell, newlines end the row. */
function escapeTableCell(value: string): string {
  return value.split("|").join("\\|").split("\n").join(" ").split("\r").join("");
}

/** One CSV field — quoted only when it has to be. */
function escapeCsvField(value: string): string {
  const needsQuote =
    value.includes('"') || value.includes(",") || value.includes("\n") || value.includes("\r");
  return needsQuote ? `"${value.split('"').join('""')}"` : value;
}

function truncateLines(text: string, cap: number): { text: string; omitted: number } {
  const lines = text.split("\n");
  if (lines.length <= cap) return { text, omitted: 0 };
  return { text: lines.slice(0, cap).join("\n"), omitted: lines.length - cap };
}

/** The honest tail line under a truncated block. */
function moreRowsNote(omitted: number): string {
  return `_…and ${omitted.toLocaleString()} more rows — download CSV from the artifact card._`;
}

/** Fence language for a code artifact, from the artifact's own language/type/name. */
function codeLanguage(art: AppendixArtifact): string {
  if (art.language) return String(art.language).toLowerCase();
  const type = (art.type || "").toLowerCase();
  if (type.includes("apex")) return "apex";
  if (type.includes("soql") || type.includes("sql")) return "sql";
  if (type.includes("xml") || type.includes("flow")) return "xml";
  if (type.includes("lwc") || type.includes("javascript")) return "javascript";
  const name = (art.name || "").toLowerCase();
  if (name.endsWith(".cls") || name.endsWith(".trigger")) return "apex";
  if (name.endsWith(".xml")) return "xml";
  if (name.endsWith(".js")) return "javascript";
  if (name.endsWith(".sql")) return "sql";
  return "";
}

/** A fenced block whose content might itself contain ``` — widen the fence rather
 *  than let the artifact break out of it and corrupt the rest of the document. */
function fence(body: string, lang: string): string {
  const marker = body.includes("```") ? "````" : "```";
  return `${marker}${lang}\n${body}\n${marker}`;
}

/** Render the row set as a markdown table (narrow) or a CSV block (wide), spending
 *  at most `rowCap` rows. */
function renderRows(
  rows: Array<Record<string, unknown>>,
  declaredColumns: unknown,
  rowCap: number,
): string {
  const columns = dataColumns(rows, declaredColumns);
  if (columns.length === 0) return "";
  const capped = rows.slice(0, rowCap);
  const cell = (row: Record<string, unknown>, column: string) =>
    displayCell(readDataCell(row, column));

  if (columns.length <= MAX_TABLE_COLUMNS) {
    const lines = [
      `| ${columns.map(escapeTableCell).join(" | ")} |`,
      `| ${columns.map(() => "---").join(" | ")} |`,
      ...capped.map((row) => `| ${columns.map((c) => escapeTableCell(cell(row, c))).join(" | ")} |`),
    ];
    return lines.join("\n");
  }
  const csv = [
    columns.map(escapeCsvField).join(","),
    ...capped.map((row) => columns.map((c) => escapeCsvField(cell(row, c))).join(",")),
  ].join("\n");
  return fence(csv, "csv");
}

/** Pretty-print JSON that carries no row set, so the appendix shows structure
 *  rather than one 4 kB line. */
function renderJsonBlock(parsed: unknown): string {
  const pretty = JSON.stringify(parsed, null, 2);
  const { text, omitted } = truncateLines(pretty, APPENDIX_LINE_CAP);
  return omitted > 0
    ? `${fence(text, "json")}\n\n_…and ${omitted.toLocaleString()} more lines — open the artifact in the app for the full payload._`
    : fence(text, "json");
}

/**
 * The body of one appendix entry (everything below its heading).
 *
 * `rowBudget` is what is LEFT of the document-wide budget when this entry is
 * reached; the entry spends at most that, and at most `APPENDIX_ROW_CAP`. It
 * reports `rowsUsed` so the caller can debit the budget.
 */
export function renderAppendixBody(
  art: AppendixArtifact,
  rowBudget: number = APPENDIX_ROW_CAP,
): { body: string; rowCount: number | null; rowsUsed: number } {
  const content = typeof art.content === "string" ? art.content : "";
  if (!content.trim()) return { body: "_This artifact carried no content._", rowCount: null, rowsUsed: 0 };

  const trimmed = content.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    let parsed: unknown = null;
    let ok = false;
    try {
      parsed = JSON.parse(trimmed);
      ok = true;
    } catch {
      /* not JSON after all — fall through to the code/text paths */
    }
    if (ok) {
      const picked = pickDataRows(parsed);
      if (picked.rows.length > 0) {
        const declared = (parsed as Record<string, unknown> | null)?.columns;
        const total = Math.max(picked.total, picked.rows.length);
        const rowCap = Math.max(0, Math.min(APPENDIX_ROW_CAP, rowBudget));
        if (rowCap === 0) {
          // The document's budget was spent by earlier citations. Say which rows are
          // missing and how many — an entry that simply rendered nothing would read
          // as "this artifact was empty".
          return {
            body: `_All ${total.toLocaleString()} rows omitted — this document's evidence budget was spent by earlier citations. Download CSV from the artifact card._`,
            rowCount: picked.total,
            rowsUsed: 0,
          };
        }
        const table = renderRows(picked.rows, declared, rowCap);
        const shown = Math.min(picked.rows.length, rowCap);
        const omitted = total - shown;
        const body = omitted > 0 ? `${table}\n\n${moreRowsNote(omitted)}` : table;
        return { body, rowCount: picked.total, rowsUsed: shown };
      }
      if (picked.source === "none" && picked.total > 0) {
        // A bare aggregate: the number IS the evidence, and it costs no budget.
        return {
          body: `Aggregate result: **${picked.total.toLocaleString()}** (no row data returned).`,
          rowCount: picked.total,
          rowsUsed: 0,
        };
      }
      return { body: renderJsonBlock(parsed), rowCount: null, rowsUsed: 0 };
    }
  }

  const lang = codeLanguage(art);
  const { text, omitted } = truncateLines(content, APPENDIX_LINE_CAP);
  const tail = omitted > 0 ? `\n\n_…and ${omitted.toLocaleString()} more lines — open the artifact in the app for the full content._` : "";
  // Prose evidence stays prose; anything with a resolvable language is fenced so
  // indentation and braces survive the PDF/DOCX renderers.
  if (!lang) return { body: `${text}${tail}`, rowCount: null, rowsUsed: 0 };
  return { body: `${fence(text, lang)}${tail}`, rowCount: null, rowsUsed: 0 };
}

/** One heading + body for a cited artifact (or an honest stub when it failed to
 *  load), plus the rows it spent from the document budget. */
export function renderAppendixEntry(
  citation: ArtifactCitation,
  art: AppendixArtifact | null,
  index: number,
  rowBudget: number = APPENDIX_ROW_CAP,
): { text: string; rowsUsed: number } {
  const label = sanitizeArtifactLabel(citation.label, citation.id.slice(0, 8));
  if (!art) {
    return {
      text: [
        `### ${index}. ${label}`,
        "",
        `_Content unavailable — this artifact could not be loaded at export time (id \`${citation.id}\`)._`,
      ].join("\n"),
      rowsUsed: 0,
    };
  }
  const name = sanitizeArtifactLabel(art.name || label, label);
  const { body, rowCount, rowsUsed } = renderAppendixBody(art, rowBudget);
  const heading =
    rowCount !== null
      ? `### ${index}. ${name} — ${rowCount.toLocaleString()} rows`
      : `### ${index}. ${name}`;
  const meta: string[] = [];
  if (art.type) meta.push(art.type);
  if (label && label !== name) meta.push(`cited as "${label}"`);
  const metaLine = meta.length ? `_${meta.join(" · ")}_\n\n` : "";
  return { text: `${heading}\n\n${metaLine}${body}`, rowsUsed };
}

/** Resolve `ids` through `resolve`, `limit` at a time (rule: never serial when the
 *  work is independent — these are independent GETs). Order is preserved. */
async function resolveAll(
  citations: ArtifactCitation[],
  resolve: AppendixResolver,
  limit = 6,
): Promise<Array<AppendixArtifact | null>> {
  const out: Array<AppendixArtifact | null> = new Array(citations.length).fill(null);
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const i = cursor++;
      if (i >= citations.length) return;
      try {
        out[i] = await resolve(citations[i].id);
      } catch {
        out[i] = null; // rendered as the "could not be loaded" stub, never silently dropped
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, citations.length) }, worker));
  return out;
}

/**
 * The whole appendix section, or "" when the report cites nothing. Takes an
 * injected resolver so the assembly can be tested without a network or a store.
 */
export async function buildCitedEvidenceAppendix(
  citations: ArtifactCitation[],
  resolve: AppendixResolver,
  totalRowBudget: number = APPENDIX_TOTAL_ROW_BUDGET,
): Promise<string> {
  if (citations.length === 0) return "";
  // Fetch in parallel, RENDER in citation order — the budget is spent first-cited
  // first, which is only meaningful as a sequential walk.
  const resolved = await resolveAll(citations, resolve);
  let remaining = totalRowBudget;
  const entries = citations.map((c, i) => {
    const { text, rowsUsed } = renderAppendixEntry(c, resolved[i], i + 1, remaining);
    remaining = Math.max(0, remaining - rowsUsed);
    return text;
  });
  return [`## ${APPENDIX_HEADING}`, "", entries.join("\n\n")].join("\n");
}

/**
 * Artifact contents fetched for an export, keyed by id. An export of three formats
 * in a row (the user clicks Markdown, then PDF) would otherwise refetch every cited
 * artifact each time; the content is immutable once the run has finished, so a hit
 * is always correct.
 *
 * LRU with BOTH an entry cap and a byte cap, per the project's bounded-cache rule.
 * Entries here are whole artifact bodies — a long weave session citing exhaustive
 * scans can hold tens of megabytes, and an unbounded Map of them is a leak that only
 * shows up in the sessions that matter most.
 */
const CACHE_MAX_ENTRIES = 40;
const CACHE_MAX_BYTES = 8 * 1024 * 1024;
const contentCache = new Map<string, AppendixArtifact>();
let cacheBytes = 0;

function cacheEntryBytes(art: AppendixArtifact): number {
  return art.content.length;
}

function cachePut(art: AppendixArtifact): void {
  const existing = contentCache.get(art.id);
  if (existing) {
    contentCache.delete(art.id);
    cacheBytes -= cacheEntryBytes(existing);
  }
  contentCache.set(art.id, art);
  cacheBytes += cacheEntryBytes(art);
  // Map preserves insertion order, so the first key is the least recently used.
  while (contentCache.size > CACHE_MAX_ENTRIES || cacheBytes > CACHE_MAX_BYTES) {
    const oldest = contentCache.keys().next();
    if (oldest.done) break;
    const evicted = contentCache.get(oldest.value);
    contentCache.delete(oldest.value);
    if (evicted) cacheBytes -= cacheEntryBytes(evicted);
    if (contentCache.size === 0) {
      cacheBytes = 0;
      break;
    }
  }
}

function cacheGet(id: string): AppendixArtifact | undefined {
  const hit = contentCache.get(id);
  if (!hit) return undefined;
  // Re-insert so a repeatedly cited artifact stays the most recently used.
  contentCache.delete(id);
  contentCache.set(id, hit);
  return hit;
}

/** The production resolver: the run's artifact list supplies name/type, the API
 *  supplies content (cached). */
export const fetchAppendixArtifact: AppendixResolver = async (id) => {
  const hit = cacheGet(id);
  if (hit) return hit;
  const art = await getArtifact(id);
  const resolved: AppendixArtifact = {
    id,
    name: art.name,
    type: art.type,
    language: art.language,
    content: art.content || "",
  };
  cachePut(resolved);
  return resolved;
};

/**
 * The exporter's entry point: takes the RAW report markdown (citations intact),
 * returns the export-ready markdown — links reduced to labels, as before, plus the
 * cited-evidence appendix. Callers that already stripped the citations get the old
 * behavior (no citations found → no appendix), which is why ResultColumn now passes
 * the raw markdown down.
 */
export async function buildExportMarkdown(
  rawMarkdown: string,
  resolve: AppendixResolver = fetchAppendixArtifact,
): Promise<string> {
  const body = exportArtifactMarkdown(rawMarkdown);
  const citations = collectArtifactCitations(rawMarkdown);
  if (citations.length === 0) return body;
  const appendix = await buildCitedEvidenceAppendix(citations, resolve);
  if (!appendix) return body;
  return `${body}\n\n---\n\n${appendix}\n`;
}
